import { format } from 'date-fns';
import { orderBy } from 'lodash';
import moment from 'moment';

import * as dto from 'client/dto';

import { LocationSearchType, LocationType } from 'components/_controls/AoiSearch/models';

import { roundToStep } from './mathHelpers';

export const pluralize = (num: number | false, label?: string, labelPlural?: string): string => {
  labelPlural = labelPlural || `${label}s`;
  return !label ? null : num === 1 ? label : labelPlural || `${label}s`;
};
export const pluralizeMomentDiffUnit = (
  num: number,
  momentUnit: moment.unitOfTime.DurationConstructor
): string => {
  switch (momentUnit) {
    case 'year':
    case 'years':
      return pluralize(num, 'year', 'years');
    case 'month':
    case 'months':
      return pluralize(num, 'month', 'months');
    case 'day':
    case 'days':
      return pluralize(num, 'day', 'days');
    default:
      return pluralize(num, momentUnit);
  }
};

export type UseEmDashFor0 = 'emdash-0' | 'emdash-0<' | false;
export const formatNumber = (
  num: number | false,
  label?: string,
  labelPlural?: string,
  emdashWhen0: UseEmDashFor0 = false
): string => {
  const labelText = pluralize(num, label, labelPlural);
  return !(
    (emdashWhen0 === 'emdash-0' && !num) ||
    (emdashWhen0 === 'emdash-0<' && num <= 0) ||
    (!num && num !== 0) ||
    isNaN(num)
  )
    ? `${num.toLocaleString()}${labelText ? ` ${labelText}` : ''}`
    : '—';
};

export const formatNumberOfCases = (num: number): string => {
  return num && num > 0
    ? formatNumber(num, 'case', 'cases', 'emdash-0')
    : num !== undefined
    ? `—`
    : `Calculating...`;
};

export const formatCaseCountPrevalence = (caseCountPrevalence: number): string => {
  const roundedPrevalence =
    caseCountPrevalence >= 99
      ? Math.round(caseCountPrevalence * 10) / 10
      : caseCountPrevalence >= 9
      ? Math.round(caseCountPrevalence * 100) / 100
      : caseCountPrevalence >= 0.001
      ? Math.round(caseCountPrevalence * 1000) / 1000
      : caseCountPrevalence > 0
      ? caseCountPrevalence
      : 0;

  return !roundedPrevalence
    ? '—'
    : roundedPrevalence >= 100
    ? Math.round(caseCountPrevalence).toLocaleString()
    : roundedPrevalence >= 10
    ? roundedPrevalence.toFixed(1)
    : roundedPrevalence >= 1
    ? roundedPrevalence.toFixed(2)
    : roundedPrevalence >= 0.001
    ? roundedPrevalence.toFixed(3)
    : '<0.001';
};

export const formatShortNumberRange = (
  min: number,
  max: number,
  label?: string,
  labelPlural?: string
): string => {
  labelPlural = labelPlural || `${label}s`;
  if (min === 0 && max === 0) return formatNumber(0, label, labelPlural);
  if (min === max) return formatNumber(min, label, labelPlural);
  return `${formatNumber(min)}-${formatNumber(max)}${label ? ` ${labelPlural}` : ''}`;
};

export const formatPercent = (value: number, outof: number): string => {
  if (outof === 0) return '0%';
  const percent = (100 * value) / outof;
  return percent < 1 ? '<1%' : `${formatNumber(Math.round(percent))}%`;
};

export const formatRatio1inX = (percent: number): string => {
  if (percent === 0) return '0';
  return `1 in ${formatNumber(Math.round(1 / percent))}`;
};

export const getTopAlphabeticalLocationList = (locations: string[], topCount: number) => {
  if (!locations || !locations.length) return '';

  const sortedLocations = orderBy(locations);
  const remainingCount = sortedLocations.length - topCount;
  switch (sortedLocations.length) {
    case 1:
      return sortedLocations[0];
    case 2:
      return `${sortedLocations[0]} and ${sortedLocations[1]}`;
    default:
      return sortedLocations.length > topCount
        ? `${sortedLocations.slice(0, topCount).join(', ')}, and ${remainingCount} more ${pluralize(
            remainingCount,
            'location',
            'locations'
          )}`
        : `${sortedLocations.slice(0, topCount - 1).join(', ')}, and ${
            sortedLocations[topCount - 1]
          }`;
  }
};

export const getTopAirportShortNameList = (airports: dto.GetAirportRiskModel[], total: number) => {
  if (!airports || !airports.length || !airports[0]) return 'No matching airports with >1% risk';
  const strOthers =
    total > 1 ? ` and ${formatNumber(total - 1, 'other airport', 'other airports')}` : '';
  return `${airports[0].name} (${airports[0].code}) ${strOthers}`;
};

export const locationTypePrint = (locationType: LocationType): string => {
  switch (locationType) {
    case LocationSearchType.City:
      return 'City/Township';
    case LocationSearchType.County:
      return 'County';
    case LocationSearchType.MetroArea:
      return 'Metropolitan Area';
    case LocationSearchType.Province:
      return 'Province/State';
    case LocationSearchType.Country:
      return 'Country';
    case LocationSearchType.Airport:
      return 'Airport';
    default:
      return 'Unknown';
  }
};

export const getLocationFullName = (geoname: dto.GetGeonameModel): string => {
  const { name, province, country, locationType } = geoname;
  return locationType === dto.LocationType.Country
    ? name
    : locationType === dto.LocationType.Province
    ? [province || name, country].filter(i => !!i).join(', ')
    : locationType === dto.LocationType.City
    ? [name, province, country].filter(i => !!i).join(', ')
    : name;
};

export const formatOAG = () => {
  const oagItinerariesDateStr = moment().subtract(4, 'months').format('MMMM yyyy');
  const oagSchedulesDateStr = format(new Date(), 'MMMM yyyy');

  return `OAG itineraries (${oagItinerariesDateStr}) and OAG schedules (${oagSchedulesDateStr})`;
};

export const formatLandscan = (meta: dto.ApplicationMetadataModel) => {
  return meta ? `Landscan (${meta.landscanDatasetYear})` : 'Landscan';
};

const numVerbose = {
  1: 'One',
  2: 'Two',
  3: 'Three',
  4: 'Four',
  5: 'Five',
  6: 'Six',
  7: 'Seven',
  8: 'Eight',
  9: 'Nine',
  10: 'Ten',
};

const formatNumberVerbose = (num: number): string => {
  if (num > 0 && num <= 10) {
    return numVerbose[num];
  }
  if (num <= 100) {
    return `${num.toLocaleString()}`;
  }
  if (num <= 999999) {
    const numberOfDigits = num.toString().length;
    const factor = Math.pow(10, numberOfDigits - 1);
    const flooredToOneSignificantDigit = Math.trunc(num / factor) * factor;
    const prefix = num === flooredToOneSignificantDigit ? '' : 'Over ';
    return `${prefix}${flooredToOneSignificantDigit.toLocaleString()}`;
  }
  if (num > 999999 && num <= 99999999) {
    const numberOfDigits = num.toString().length;
    const factor = Math.pow(10, numberOfDigits - 2);
    const flooredToTwoSignificantDigits = Math.trunc(num / factor) * factor;
    const prefix = num === flooredToTwoSignificantDigits ? '' : 'Over ';
    const millionInTwoSignificantDigits = flooredToTwoSignificantDigits / 1000000;
    return `${prefix}${millionInTwoSignificantDigits.toLocaleString()} million`;
  }

  return num == 100000000 ? '100 million' : 'Over 100 million';
};

export const formatNumberOfCasesVerbose = (num: number, label: string): string => {
  if (num && num > 0) {
    const suffix = num === 1 ? ` ${label}` : ` ${label}s`;
    return formatNumberVerbose(Math.round(num)) + suffix;
  }
  return `—`;
};

/**
 * Takes a fraction of a number and converts it into it's %age string
 * Ex: inNum = 0.50, returns 50%
 *     inNum = 0.501, returns 50.1%
 *     inNum = 0.0001, returns <1%
 *     inNum = 0.0001 & showSubOnePercent = true, returns 0.01%
 *     inNum = 0, returns 0%
 *     inNum = null, returns null
 * @param inNum the number to turn into the percentage string
 * @param roundedDecimals the number of decimals to round to
 * @param showSubOnePercent true if you want to see %s that are below 1% (EXCLUDING 0%)
 */
export const convertToStringPercentage = (
  inNum: number,
  roundedDecimals?: number,
  showSubOnePercent: boolean = false,
  keepTrailingZeros: boolean = false
) => {
  if (inNum == null) return null;
  if (inNum === 0) return '0%';

  const calculatedPercent = inNum * 100;
  return calculatedPercent < 1 && !showSubOnePercent
    ? `<1%`
    : roundedDecimals != null && !keepTrailingZeros
    ? `${+calculatedPercent.toFixed(roundedDecimals)}%`
    : roundedDecimals != null
    ? `${calculatedPercent.toFixed(roundedDecimals)}%`
    : `${calculatedPercent}%`;
};

/**
 * Takes a number an rounds it to the nearest 100x where X is either thousand, million or billion if that number is over 1000
 * Ex: inNum = 0, returns 0
 *     inNum = 10, returns 10
 *     inNum = 100, returns 100
 *     inNum = 1000, returns 1k
 *     inNum = 100000, returns 100k
 * @param inNum The number to be rounded into it's shortest form
 */
export const formatNumberWithSuffix = (inNum: number): string => {
  if (inNum == null) return null;
  if (inNum < 1000 && inNum > -1000) return `${inNum}`;

  if (inNum < 10000 && inNum > -10000) return `${+(Math.round(inNum) / 1000).toFixed(1)}K`;

  if (inNum < 1000000 && inNum > -1000000) {
    const rounded = roundToStep(inNum / 1000, 0.5);

    // If it rounds up to 1000 (-1000) we just need to have 1 on the higher letter type
    return rounded === 1000 ? `1M` : rounded === -1000 ? '-1M' : `${rounded}K`;
  }

  if (inNum < 1000000000 && inNum > -1000000000) {
    const rounded = roundToStep(inNum / 1000000, 0.5);

    // If it rounds up to 1000 (-1000) we just need to have 1 on the higher letter type
    return rounded === 1000 ? `1B` : rounded === -1000 ? '-1B' : `${rounded}M`;
  }

  if (inNum < 1000000000000 && inNum > -1000000000000)
    return `${roundToStep(inNum / 1000000000, 0.5)}B`;

  return `${inNum}`;
};

export const oxford = (arr: string[], conjunction = 'and', ifEmpty = '') => {
  // NOTE: for comma before end i.e. "a, b, and c" go to https://stackoverflow.com/a/15069646/3353146
  if (!arr) return ifEmpty;
  const l = arr.length;
  if (!l) return ifEmpty;
  if (l < 2) return arr[0];
  if (l < 3) return arr.join(` ${conjunction} `);
  return `${arr.slice(0, -1).join(', ')} ${conjunction} ${arr[l - 1]}`;
};
